import { Box } from "@mui/material";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { JSONValue } from "../../../../models/fibo";
import type { VglScalar } from "../../../../utils/vglTokens";
import { vglNumber } from "../../../../utils/vglTokens";
import { NumberInputWithSteppers } from "./BoundingBoxInput";
import styles from "./FiboPromptTree.module.scss";

// Coordinates may be model tokens ("<coord_33.2>") — read only through `vglNumber`.
export interface KeypointValue {
	name: string;
	point: { x: VglScalar; y: VglScalar };
}

interface KeypointTreeNodeProps {
	v: JSONValue;
	path: string[];
	subtreeChanged: boolean;
	isPreserved: boolean;
	isEditable?: boolean;
	onValueChange?: (path: string[], value: JSONValue) => void;
}

export const KeypointTreeNode = ({
	v,
	path,
	subtreeChanged,
	isPreserved,
	isEditable,
	onValueChange,
}: KeypointTreeNodeProps) => {
	const kp = v as unknown as KeypointValue;
	const pointX = vglNumber(kp.point.x) ?? 0;
	const pointY = vglNumber(kp.point.y) ?? 0;

	const [localPoint, setLocalPoint] = useState({ x: Math.round(pointX), y: Math.round(pointY) });
	const isInternalUpdate = useRef(false);

	useEffect(() => {
		if (isInternalUpdate.current) {
			isInternalUpdate.current = false;
			return;
		}
		const rounded = { x: Math.round(pointX), y: Math.round(pointY) };
		if (rounded.x !== localPoint.x || rounded.y !== localPoint.y) {
			setLocalPoint(rounded);
		}
	}, [pointX, pointY]);

	const handleChange = (axis: "x" | "y", val: number) => {
		const clamped = Math.max(0, Math.min(100, Math.round(val)));
		const updated = { ...localPoint, [axis]: clamped };
		setLocalPoint(updated);
		isInternalUpdate.current = true;
		onValueChange?.(path, { ...kp, point: updated } as unknown as JSONValue);
	};

	return (
		<Box
			className={clsx(styles.row, {
				[styles.isChanged]: subtreeChanged && !isPreserved,
				[styles.isPreserved]: isPreserved,
			})}
			onClick={(e) => e.stopPropagation()}
		>
			<Box className={styles.keypointRow}>
				<span className={styles.keypointLabel}>{kp.name}:</span>
				<Box className={styles.boundingBoxInputs}>
					<span>[</span>
					<NumberInputWithSteppers
						value={localPoint.x}
						onChange={(val) => handleChange("x", val)}
						disabled={!isEditable}
						min={0}
						max={100}
					/>
					<span>,</span>
					<NumberInputWithSteppers
						value={localPoint.y}
						onChange={(val) => handleChange("y", val)}
						disabled={!isEditable}
						min={0}
						max={100}
					/>
					<span>]</span>
				</Box>
			</Box>
		</Box>
	);
};
